// components/dashboard/employer/profile/EmployerProfile.tsx
// 🎨 Employer profile (employer + unit editable, organisation read-only)
import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext.tsx";
import { useEmployer } from "@/contexts/EmployerContext.tsx";
import { useToast } from "@/hooks/use-toast";
import {
    Building,
    Building2,
    CheckCircle2,
    Mail,
    MapPin,
    Phone,
    Save,
    User,
    Users
} from "lucide-react";

// Import Nexilink components
import { ActionButton, FloatingActionBar, LoadingState, ErrorState, IconChip } from "@/components/custom-ui";

// Card styles
import { NEXILINK_CARD_WHITE_ELEVATED, NEXILINK_SPACING, NEXILINK_GAP } from "@/lib/nexilink-card-styles";

const EmployerProfile: React.FC = () => {
    const { user } = useAuth();
    const { toast } = useToast();
    const {
        employerDetails,
        updateEmployerProfile,
        updateUnit,
        isLoadingProfile,
        isSavingProfile,
        profileError,
        getEmployerProfile
    } = useEmployer();

    const [hasChanges, setHasChanges] = useState(false);

    // Employer fields (editable)
    const [employerData, setEmployerData] = useState({
        job_title: "",
        is_active: true
    });

    // Unit fields (editable)
    const [unitData, setUnitData] = useState({
        name: "",
        city: "",
        description: "",
        contact_name: "",
        contact_email: "",
        contact_phone: "",
        is_active: true
    });

    // Load profile on mount
    useEffect(() => {
        if (!employerDetails) {
            getEmployerProfile();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sync form state with context data
    useEffect(() => {
        if (!employerDetails) return;

        setEmployerData({
            job_title: employerDetails.employer.job_title || "",
            is_active: employerDetails.employer.is_active
        });

        setUnitData({
            name: employerDetails.unit.name || "",
            city: employerDetails.unit.city || "",
            description: employerDetails.unit.description || "",
            contact_name: employerDetails.unit.contact_name || "",
            contact_email: employerDetails.unit.contact_email || "",
            contact_phone: employerDetails.unit.contact_phone || "",
            is_active: employerDetails.unit.is_active
        });

        // Fresh profile load should not be treated as "unsaved changes"
        setHasChanges(false);
    }, [employerDetails]);

    const updateEmployerField = (field: keyof typeof employerData, value: string | boolean) => {
        setEmployerData((prev) => ({ ...prev, [field]: value }));
        setHasChanges(true);
    };

    const updateUnitField = (field: keyof typeof unitData, value: string | boolean) => {
        setUnitData((prev) => ({ ...prev, [field]: value }));
        setHasChanges(true);
    };

    const handleSave = async () => {
        try {
            // Validation (keep lightweight + user-friendly)
            if (!employerData.job_title?.trim()) throw new Error("Jobbtitel är obligatorisk");
            if (!unitData.name?.trim()) throw new Error("Enhetsnamn är obligatoriskt");
            if (!unitData.city?.trim()) throw new Error("Stad är obligatorisk");
            if (!unitData.contact_email?.trim()) throw new Error("Kontakt-e-post är obligatorisk");

            await updateEmployerProfile({
                job_title: employerData.job_title.trim(),
                is_active: employerData.is_active
            });

            await updateUnit({
                name: unitData.name.trim(),
                city: unitData.city.trim(),
                description: unitData.description?.trim() || undefined,
                contact_name: unitData.contact_name?.trim() || undefined,
                contact_email: unitData.contact_email.trim(),
                contact_phone: unitData.contact_phone?.trim() || undefined,
                is_active: unitData.is_active
            });

            toast({
                title: "Profil uppdaterad!",
                description: "Dina ändringar har sparats.",
                duration: 3000
            });

            setHasChanges(false);
        } catch (error: any) {
            toast({
                title: "Fel vid sparande",
                description: error?.message || "Kunde inte spara profilen",
                variant: "destructive",
                duration: 5000
            });
        }
    };

    // Loading state
    if (isLoadingProfile) return <LoadingState message="Laddar profil..." />;

    // Error state
    if (profileError) {
        return (
            <ErrorState
                title="Kunde inte ladda profilen"
                message={profileError}
                onRetry={getEmployerProfile}
            />
        );
    }

    if (!employerDetails) {
        return (
            <ErrorState
                title="Ingen profil hittades"
                message="Du har ingen arbetsgivarprofil kopplad till ditt konto."
                onRetry={getEmployerProfile}
            />
        );
    }

    const { employer, user: employerUser, organisation } = employerDetails;

    return (
        <div className={NEXILINK_SPACING}>
            {/* Layout: left column (Employer + Unit), right column (Organisation info, read-only) */}
            <div className={`grid grid-cols-1 lg:grid-cols-3 ${NEXILINK_GAP}`}>
                {/* LEFT: Employer + Unit */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Employer card (editable) */}
                    <div className={`${NEXILINK_CARD_WHITE_ELEVATED} p-6`}>
                        <div className="flex items-center gap-3 mb-6">
                            <IconChip icon={User} size="lg" />
                            <div className="min-w-0">
                                <h2 className="text-xl font-bold text-gray-900">Min profil</h2>
                                <p className="text-sm text-gray-600">Din personliga arbetsgivarprofil</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Full name (read-only) */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Namn</label>
                                <div className="px-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-xl text-gray-700">
                                    {employerUser.full_name}
                                </div>
                            </div>

                            {/* Email (read-only) */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">E-post</label>
                                <div className="px-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-xl text-gray-700">
                                    {employerUser.email}
                                </div>
                            </div>

                            {/* Job title (editable) */}
                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Jobbtitel</label>
                                <input
                                    type="text"
                                    value={employerData.job_title}
                                    onChange={(e) => updateEmployerField("job_title", e.target.value)}
                                    placeholder="t.ex. Rekryteringsansvarig, HR-chef"
                                    className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl focus:border-[#1A4D4F] focus:outline-none transition-colors"
                                />
                            </div>

                            {/* Active toggle (editable) */}
                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                                <div className="flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-xl">
                                    <div className="flex items-center gap-2 min-w-0">
                                        {employerData.is_active ? (
                                            <div className="text-green-700 font-medium flex items-center gap-2">
                                                <CheckCircle2 className="w-4 h-4" />
                                                Aktiv
                                            </div>
                                        ) : (
                                            <div className="text-gray-700 font-medium">Inaktiv</div>
                                        )}
                                        <span className="text-sm text-gray-500 hidden sm:inline">
                      (styr om du kan hantera rekrytering och kandidater)
                    </span>
                                    </div>

                                    <label className="flex items-center gap-2 text-sm text-gray-700 select-none whitespace-nowrap">
                                        <input
                                            type="checkbox"
                                            checked={employerData.is_active}
                                            onChange={(e) => updateEmployerField("is_active", e.target.checked)}
                                            className="h-4 w-4 accent-[#1A4D4F]"
                                        />
                                        Aktiv arbetsgivare
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Unit card (editable) */}
                    <div className={`${NEXILINK_CARD_WHITE_ELEVATED} p-6`}>
                        <div className="flex items-center gap-3 mb-6">
                            <IconChip icon={Building} size="lg" />
                            <div className="min-w-0">
                                <h2 className="text-xl font-bold text-gray-900">Min enhet</h2>
                                <p className="text-sm text-gray-600">
                                    Du hanterar endast din enhet (andra enheter kan finnas i samma organisation)
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Unit name */}
                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Enhetsnamn</label>
                                <input
                                    type="text"
                                    value={unitData.name}
                                    onChange={(e) => updateUnitField("name", e.target.value)}
                                    placeholder="t.ex. Barn & Unga, HR-avdelningen"
                                    className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl focus:border-[#1A4D4F] focus:outline-none transition-colors"
                                />
                            </div>

                            {/* City */}
                            <div className="md:col-span-1">
                                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                    <MapPin className="w-4 h-4" />
                                    Stad
                                </label>
                                <input
                                    type="text"
                                    value={unitData.city}
                                    onChange={(e) => updateUnitField("city", e.target.value)}
                                    placeholder="t.ex. Uppsala"
                                    className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl focus:border-[#1A4D4F] focus:outline-none transition-colors"
                                />
                            </div>

                            {/* Unit active toggle */}
                            <div className="md:col-span-1">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                                <div className="flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-xl">
                                    <div className="flex items-center gap-2 min-w-0">
                                        {unitData.is_active ? (
                                            <div className="text-green-700 font-medium flex items-center gap-2">
                                                <CheckCircle2 className="w-4 h-4" />
                                                Aktiv
                                            </div>
                                        ) : (
                                            <div className="text-gray-700 font-medium">Inaktiv</div>
                                        )}
                                    </div>

                                    <label className="flex items-center gap-2 text-sm text-gray-700 select-none whitespace-nowrap">
                                        <input
                                            type="checkbox"
                                            checked={unitData.is_active}
                                            onChange={(e) => updateUnitField("is_active", e.target.checked)}
                                            className="h-4 w-4 accent-[#1A4D4F]"
                                        />
                                        Aktiv enhet
                                    </label>
                                </div>
                            </div>

                            {/* Unit description */}
                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Beskrivning <span className="text-gray-400 font-normal">(valfritt)</span>
                                </label>
                                <textarea
                                    value={unitData.description}
                                    onChange={(e) => updateUnitField("description", e.target.value)}
                                    placeholder="Kort beskrivning av enhetens verksamhet och ansvar..."
                                    rows={4}
                                    className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl focus:border-[#1A4D4F] focus:outline-none transition-colors resize-none"
                                />
                            </div>

                            {/* Contact name */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Kontaktperson <span className="text-gray-400 font-normal">(valfritt)</span>
                                </label>
                                <input
                                    type="text"
                                    value={unitData.contact_name}
                                    onChange={(e) => updateUnitField("contact_name", e.target.value)}
                                    placeholder="t.ex. Anna Andersson"
                                    className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl focus:border-[#1A4D4F] focus:outline-none transition-colors"
                                />
                            </div>

                            {/* Contact phone */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                    <Phone className="w-4 h-4" />
                                    Telefon <span className="text-gray-400 font-normal">(valfritt)</span>
                                </label>
                                <input
                                    type="tel"
                                    value={unitData.contact_phone}
                                    onChange={(e) => updateUnitField("contact_phone", e.target.value)}
                                    placeholder="+46 70 123 45 67"
                                    className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl focus:border-[#1A4D4F] focus:outline-none transition-colors"
                                />
                            </div>

                            {/* Contact email */}
                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                    <Mail className="w-4 h-4" />
                                    Kontakt-e-post
                                </label>
                                <input
                                    type="email"
                                    value={unitData.contact_email}
                                    onChange={(e) => updateUnitField("contact_email", e.target.value)}
                                    placeholder="kontakt@organisation.se"
                                    className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl focus:border-[#1A4D4F] focus:outline-none transition-colors"
                                />
                            </div>

                            {/* Helpful info */}
                            <div className="md:col-span-2 p-4 bg-[#A4C3B2]/30 border-2 border-[#A4C3B2] rounded-xl">
                                <p className="text-sm text-[#1A4D4F]">
                                    <strong>Tips:</strong> Dessa kontaktuppgifter används när kandidater vill få kontakt med din enhet.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* RIGHT: Organisation card (read-only, info) */}
                <aside className="lg:col-span-1">
                    <div className={`${NEXILINK_CARD_WHITE_ELEVATED} p-6 lg:sticky lg:top-6`}>
                        <div className="flex items-center gap-3 mb-6">
                            <IconChip icon={Building2} size="lg" />
                            <div className="min-w-0">
                                <h2 className="text-xl font-bold text-gray-900">Organisation</h2>
                                <p className="text-sm text-gray-600">Information (endast visning)</p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <div className="text-sm font-semibold text-gray-700 mb-1">Organisationsnamn</div>
                                <div className="text-sm text-gray-900">{organisation.name}</div>
                            </div>

                            {!!organisation.org_number && (
                                <div>
                                    <div className="text-sm font-semibold text-gray-700 mb-1">Organisationsnummer</div>
                                    <div className="text-sm text-gray-900">{organisation.org_number}</div>
                                </div>
                            )}

                            {(!!organisation.address || !!organisation.postal_code || !!organisation.city) && (
                                <div>
                                    <div className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
                                        <MapPin className="w-4 h-4" />
                                        Adress
                                    </div>
                                    <div className="text-sm text-gray-900">
                                        {organisation.address && <div>{organisation.address}</div>}
                                        {(organisation.postal_code || organisation.city) && (
                                            <div>
                                                {[organisation.postal_code, organisation.city].filter(Boolean).join(" ")}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {!!organisation.contact_email && (
                                <div>
                                    <div className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
                                        <Mail className="w-4 h-4" />
                                        Kontakt-e-post
                                    </div>
                                    <div className="text-sm text-gray-900">{organisation.contact_email}</div>
                                </div>
                            )}

                            {!!organisation.contact_phone && (
                                <div>
                                    <div className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
                                        <Phone className="w-4 h-4" />
                                        Kontakt-telefon
                                    </div>
                                    <div className="text-sm text-gray-900">{organisation.contact_phone}</div>
                                </div>
                            )}

                            <div className="mt-2 p-4 bg-gray-50 border-2 border-gray-100 rounded-xl">
                                <div className="flex items-start gap-3">
                                    <IconChip icon={Users} size="sm" tone="neutral" />
                                    <p className="text-sm text-gray-700">
                                        <strong>OBS:</strong> Organisationsinformation hanteras av Nexilink-admins. Kontakta support om något behöver uppdateras.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </aside>
            </div>

            {/* Save button — inline, floats to bottom when scrolled past */}
            <FloatingActionBar>
                <ActionButton
                    icon={Save}
                    onClick={handleSave}
                    disabled={!hasChanges}
                    loading={isSavingProfile}
                    className="flex-1"
                >
                    {hasChanges ? 'Spara ändringar' : 'Inga ändringar att spara'}
                </ActionButton>
            </FloatingActionBar>
        </div>
    );
};

export default EmployerProfile;
